import { f7ready } from 'framework7-react';
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';

import { useCityById, useCurrentUserCity } from '@hooks/useCurrentUserLocation';

import { EntertainerDashboardAction, useManagedHistory, useRouter } from '@router/index';

import { useDialog } from '@components/layout/Dialog/useDialog';
import { Popup } from '@components/layout/Popup/Popup';
import { AddFeaturedVideoPopup } from '@components/overlays/AddFeaturedVideoPopup';

import { RecommendSection, RecommendationsSection } from '@screens/CommunityScreen/CommunityScreen';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { AddFeaturedPhotoPopup } from '@screens/entertainer/components/FeaturedPhotoManagementSection';
import { AddSeriesEventPopup } from '@screens/series/SeriesScreen/SeriesScreen';

const AddCityDragSeriesEventPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  cityId: string;
  seriesId?: string;
}> = ({ isVisible, onClose, cityId, seriesId: seriesIdOverride }) => {
  const { city } = useCityById(cityId);
  // const { createAuthGateHandler } = useAuthGate();
  // const { openConfirmDialog } = useDialog();
  // const { entertainer } = useCurrentEntertainerQuery();

  // const handleAddEvent = () => {
  //   createAuthGateHandler(() => {}, {
  //     entertainerCallback: () => {},
  //     cityId,
  //   });
  // };

  // const handleAddUnauthShow = () => {
  //   openConfirmDialog({
  //     title: 'Add An Event',
  //     text: 'We currently only allow entertainers to add events. Would you like to continue as an entertainer?',
  //     buttonOk: 'Yes',
  //     onConfirm: handleAddEvent,
  //   });
  // };

  // useEffect(() => {
  //   if (isVisible && !entertainer?._id) {
  //     handleAddUnauthShow();
  //   } else if (!isVisible) {
  //     onClose();
  //   }
  // }, [isVisible, entertainer?._id]);

  const seriesId = seriesIdOverride ?? city?.featuredSeries?.drag;

  if (!seriesId) {
    return <></>;
  }

  return (
    <AddSeriesEventPopup isVisible={isVisible} seriesId={seriesId} city={city} onClose={onClose} />
  );
};

const RecommendationPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  cityId: string;
  hideQuestionOptions?: boolean;
  initialQuestionSlug?: string;
}> = ({ isVisible, onClose, cityId, hideQuestionOptions, initialQuestionSlug }) => {
  const [backlinkUrl, setBacklinkUrl] = useState<string>();

  useEffect(() => {
    if (isVisible) {
      f7ready((f7) => {
        try {
          const mainRouter = f7.views.main.router;
          const currentPath = mainRouter.history[mainRouter.history.length - 1];

          setBacklinkUrl(currentPath);
        } catch (e) {}
      });
    }
  }, [isVisible]);

  return (
    <Popup isVisible={isVisible} onClose={onClose}>
      <Popup.Content>
        {cityId && (
          <RecommendSection
            isListVisible
            hideQuestionOptions={hideQuestionOptions}
            initialQuestionSlug={initialQuestionSlug}
            cityId={cityId}
            back={backlinkUrl ?? '/'}
          />
        )}
      </Popup.Content>
    </Popup>
  );
};

interface StateOptions {
  seriesId?: string;
  cityId?: string;
  initialQuestionSlug?: string;
  hideQuestionOptions?: boolean;
  isEntertainerOptional?: boolean;
  callback?: () => void;
}

interface EntertainerEngagementContext {
  openAddLookPopup: (state?: StateOptions) => void;
  openAddVideoPopup: (state?: StateOptions) => void;
  openAddShowPopup: (state?: StateOptions) => void;
  openGatedAddShowPopup: (state?: StateOptions) => void;
  openRecommendationPopup: (state?: StateOptions) => void;
}

const useUnauthAddShowPopup = (
  openAddShowPopup: EntertainerEngagementContext['openAddShowPopup'],
) => {
  const { createAuthGateHandler } = useAuthGate();
  const { openConfirmDialog } = useDialog();
  const { entertainer } = useCurrentEntertainerQuery();

  const openGatedAddShowPopup = useCallback(
    (state: StateOptions) => {
      const callback = async () => {
        openAddShowPopup(state);
      };

      if (entertainer?._id || state?.isEntertainerOptional) {
        callback();
      } else {
        openConfirmDialog({
          title: 'Add An Event',
          text: 'We currently only allow entertainers to add events. Would you like to continue as an entertainer?',
          buttonOk: 'Yes',
          onConfirm: () =>
            createAuthGateHandler(callback, {
              entertainerCallback: () => {},
              cityId: state?.cityId,
            }),
        });
      }
    },
    [entertainer?._id, createAuthGateHandler],
  );

  return { openGatedAddShowPopup };
};

const entertainerEngagementContext = createContext<EntertainerEngagementContext>({
  openAddLookPopup: () => {},
  openAddVideoPopup: () => {},
  openAddShowPopup: () => {},
  openGatedAddShowPopup: () => {},
  openRecommendationPopup: () => {},
});

enum EntertainerEngagementPopup {
  AddLook = 'AddLook',
  AddVideo = 'AddVideo',
  MakeRec = 'MakeRec',
  AddShow = 'AddShow',
}

export const useEntertainerEngagementContext = () => {
  const context = useContext(entertainerEngagementContext);

  if (!context) {
    throw new Error(
      'useEntertainerEngagementContext must be used within an EntertainerEngagementProvider',
    );
  }

  return context;
};

export const EntertainerEngagementProvider: FC<PropsWithChildren> = ({ children }) => {
  const { currentCity } = useCurrentUserCity();
  const currentCityId = currentCity?._id;
  const [visiblePopup, setVisiblePopup] = useState<EntertainerEngagementPopup>();
  const [options, setOptions] = useState<StateOptions>({});
  const cityId = options?.cityId ?? currentCityId;

  const handleClosePopups = () => {
    options?.callback?.();
    setVisiblePopup(undefined);
    setOptions({});
  };

  const openAddLookPopup = (state: StateOptions) => {
    setVisiblePopup(EntertainerEngagementPopup.AddLook);
    setOptions(state);
  };

  const openAddVideoPopup = (state: StateOptions) => {
    setVisiblePopup(EntertainerEngagementPopup.AddVideo);
    setOptions(state);
  };

  const openAddShowPopup = (state: StateOptions) => {
    setVisiblePopup(EntertainerEngagementPopup.AddShow);
    setOptions(state);
  };

  const openRecommendationPopup = (state: StateOptions) => {
    setVisiblePopup(EntertainerEngagementPopup.MakeRec);
    setOptions(state);
  };

  const { openGatedAddShowPopup } = useUnauthAddShowPopup(openAddShowPopup);

  const value = useMemo(
    () => ({
      openAddVideoPopup,
      openAddLookPopup,
      openAddShowPopup,
      openGatedAddShowPopup,
      openRecommendationPopup,
    }),
    [openGatedAddShowPopup],
  );

  return (
    <entertainerEngagementContext.Provider value={value}>
      {children}

      <AddFeaturedVideoPopup
        isVisible={cityId && visiblePopup === EntertainerEngagementPopup.AddVideo}
        cityId={cityId}
        onClose={handleClosePopups}
      />

      <AddFeaturedPhotoPopup
        isVisible={cityId && visiblePopup === EntertainerEngagementPopup.AddLook}
        cityId={cityId}
        onClose={handleClosePopups}
      />

      <AddCityDragSeriesEventPopup
        isVisible={cityId && visiblePopup === EntertainerEngagementPopup.AddShow}
        cityId={cityId}
        seriesId={options?.seriesId}
        onClose={handleClosePopups}
      />

      <RecommendationPopup
        isVisible={cityId && visiblePopup === EntertainerEngagementPopup.MakeRec}
        cityId={cityId}
        hideQuestionOptions={options?.hideQuestionOptions}
        initialQuestionSlug={options?.initialQuestionSlug}
        onClose={handleClosePopups}
      />
    </entertainerEngagementContext.Provider>
  );
};
